/**
 * Cross-platform vibration utility for both iOS and Android
 */

// Variable declarations
let audioContext = null;
let audioContextInitialized = false;
let vibrationInitialized = false;

// Default values
const DEFAULT_VIBRATION_DURATION = 20; // in milliseconds

// Detect iOS device
const isIOS = () => {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

// Check if we're running in Telegram WebApp
const isTelegramWebApp = () => {
  return (
    typeof window !== "undefined" && window.Telegram && window.Telegram.WebApp
  );
};

// Check if Telegram haptic feedback is available
const isTelegramHapticAvailable = () => {
  return (
    isTelegramWebApp() &&
    window.Telegram.WebApp.HapticFeedback &&
    typeof window.Telegram.WebApp.HapticFeedback.impactOccured === "function"
  );
};

// Constants
const HAPTIC_TYPES = {
  IMPACT_LIGHT: "light",
  IMPACT_MEDIUM: "medium",
  IMPACT_HEAVY: "heavy",
  NOTIFICATION_SUCCESS: "success",
  NOTIFICATION_WARNING: "warning",
  NOTIFICATION_ERROR: "error",
};

// Auto-initialize vibration on first user interaction
if (typeof window !== "undefined") {
  const setupVibrateInit = () => {
    if (vibrationInitialized) return;

    const initOnFirstInteraction = () => {
      initVibration();
      // Clean up listeners after first interaction
      ["touchstart", "click", "keydown"].forEach((event) => {
        document.removeEventListener(event, initOnFirstInteraction, {
          passive: true,
        });
      });
    };

    // Set up event listeners for first interaction
    ["touchstart", "click", "keydown"].forEach((event) => {
      document.addEventListener(event, initOnFirstInteraction, {
        passive: true,
      });
    });
  };

  // Check if document is ready, then set up
  if (
    document.readyState === "complete" ||
    document.readyState === "interactive"
  ) {
    setupVibrateInit();
  } else {
    document.addEventListener("DOMContentLoaded", setupVibrateInit);
  }
}

/**
 * Initialize vibration to be used later
 * Must be called in response to a user gesture
 */
export function initVibration() {
  if (vibrationInitialized) return true;

  // Set initialized flag
  vibrationInitialized = true;

  // For iOS devices in Telegram WebApp, try haptic feedback
  if (isIOS() && isTelegramHapticAvailable()) {
    try {
      // Try a subtle test vibration to initialize
      window.Telegram.WebApp.HapticFeedback.impactOccured("light");
      return true;
    } catch (error) {
      console.error("Error initializing Telegram haptic feedback:", error);
    }
  }

  // Try to initialize audio context for iOS fallback
  if (isIOS() && window.AudioContext && !audioContextInitialized) {
    try {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      if (audioContext.state === "suspended") {
        audioContext.resume();
      }
      audioContextInitialized = true;
    } catch (error) {
      console.error("Error initializing AudioContext:", error);
    }
  }

  // For standard vibration API
  if (typeof navigator !== "undefined" && navigator.vibrate) {
    try {
      // Try a subtle test vibration to initialize
      navigator.vibrate(1);
      return true;
    } catch (error) {
      console.error("Error initializing standard vibration:", error);
      return false;
    }
  }

  return false;
}

/**
 * Use device vibration or fall back to audio vibration for iOS
 * @param {number} [duration=DEFAULT_VIBRATION_DURATION] - Duration in milliseconds
 */
export function vibrate(duration = DEFAULT_VIBRATION_DURATION) {
  if (!vibrationInitialized) {
    initVibration();
  }

  // Try Telegram haptic feedback first (for iOS devices)
  if (isIOS() && isTelegramHapticAvailable()) {
    try {
      const hapticType = mapDurationToHapticType(duration);
      window.Telegram.WebApp.HapticFeedback.impactOccured(hapticType);
      return;
    } catch (e) {
      console.error("Error using Telegram haptic feedback:", e);
    }
  }

  // Try standard vibration API next
  try {
    if ("vibrate" in navigator) {
      navigator.vibrate(duration);
      return;
    }
  } catch (e) {
    console.error("Error using navigator.vibrate:", e);
  }

  // Fall back to audio vibration for iOS devices
  if (isIOS()) {
    vibrateWithAudio(duration);
  }
}

/**
 * Creates audio-based vibration effect for iOS fallback
 * @param {number} [duration=DEFAULT_VIBRATION_DURATION] - Duration in milliseconds
 */
function vibrateWithAudio(duration = DEFAULT_VIBRATION_DURATION) {
  if (!audioContextInitialized) {
    initAudioContext();
  }

  try {
    if (!window.audioContext) {
      return;
    }

    const oscillator = window.audioContext.createOscillator();
    const gainNode = window.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(window.audioContext.destination);

    // Use a very low frequency that will cause physical vibration in iPhone speakers
    oscillator.frequency.value = 1;
    gainNode.gain.value = 1;

    oscillator.start();
    setTimeout(() => {
      oscillator.stop();
    }, duration);
  } catch (e) {
    console.error("Error using audio-based vibration:", e);
  }
}

/**
 * Initialize audio context for iOS fallback
 */
function initAudioContext() {
  try {
    window.AudioContext = window.AudioContext || window.webkitAudioContext;
    window.audioContext = new AudioContext();
    audioContextInitialized = true;
  } catch (e) {
    console.error("AudioContext not supported:", e);
  }
}

/**
 * Maps vibration duration to Telegram haptic feedback type
 * @param {number} duration - Duration in milliseconds
 * @returns {string} - Haptic feedback type
 */
function mapDurationToHapticType(duration) {
  if (duration <= 10) return HAPTIC_TYPES.IMPACT_LIGHT;
  if (duration <= 30) return HAPTIC_TYPES.IMPACT_MEDIUM;
  return HAPTIC_TYPES.IMPACT_HEAVY;
}

/**
 * Success vibration - uses Telegram notification success on iOS
 */
export const vibrateSuccess = () => {
  if (isIOS() && isTelegramHapticAvailable()) {
    try {
      window.Telegram.WebApp.HapticFeedback.notificationOccured(
        HAPTIC_TYPES.NOTIFICATION_SUCCESS
      );
      return true;
    } catch (e) {
      console.error("Error using Telegram notification success:", e);
    }
  }
  return vibrate([30, 50, 30]);
};

/**
 * Error vibration - uses Telegram notification error on iOS
 */
export const vibrateError = () => {
  if (isIOS() && isTelegramHapticAvailable()) {
    try {
      window.Telegram.WebApp.HapticFeedback.notificationOccured(
        HAPTIC_TYPES.NOTIFICATION_ERROR
      );
      return true;
    } catch (e) {
      console.error("Error using Telegram notification error:", e);
    }
  }
  return vibrate(100);
};

/**
 * Game move vibration - light impact
 */
export const vibrateGameMove = () => {
  if (isIOS() && isTelegramHapticAvailable()) {
    try {
      window.Telegram.WebApp.HapticFeedback.impactOccured(
        HAPTIC_TYPES.IMPACT_LIGHT
      );
      return true;
    } catch (e) {
      console.error("Error using Telegram impact light:", e);
    }
  }
  return vibrate(20);
};

// Expose a vibrateDevice function for backward compatibility
function vibrateDevice(duration = DEFAULT_VIBRATION_DURATION) {
  return vibrate(duration);
}

export default vibrateDevice;
